import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import LogoutIcon from '@mui/icons-material/Logout';
import { exitImpersonation } from '../services/api';
import { useReportBottomBannerHeight } from '../hooks/useReportBottomBannerHeight';

interface ImpersonationBannerProps {
  /** The user whose account the admin is currently inside. */
  userName: string;
  userCea: string;
  /** The admin behind this session (from the impersonation token claim). */
  adminName: string;
}

/**
 * Fixed bottom banner shown for the whole impersonated session, so an admin
 * can never mistake whose account they are changing. "Exit" restores the
 * admin's own session server-side, then reloads into the Admin page.
 */
export default function ImpersonationBanner({ userName, userCea, adminName }: ImpersonationBannerProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useReportBottomBannerHeight(8);

  const handleExit = async () => {
    setBusy(true);
    setError(null);
    try {
      await exitImpersonation();
      window.location.assign('/super-connector/admin');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to exit');
      setBusy(false);
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: (t) => t.zIndex.appBar + 1,
        bgcolor: '#2e1065', color: '#f5f3ff',
        borderTop: '2px solid #8b5cf6',
        px: { xs: 2, md: 4 }, py: 1.25,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, flexWrap: 'wrap',
        boxShadow: '0 -6px 20px rgba(0,0,0,0.25)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SupervisorAccountIcon sx={{ color: '#a78bfa' }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#f5f3ff' }}>
          You are logged in as <b>{userName}</b> ({userCea}) — admin session by {adminName}.
          Changes you make are recorded in the admin activity log.
        </Typography>
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: '#fca5a5' }}>{error}</Typography>
      )}
      <Button
        variant="contained"
        size="small"
        onClick={handleExit}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <LogoutIcon />}
        sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 700 }}
      >
        Exit to admin
      </Button>
    </Box>
  );
}
